<template>
  <DiscardChangesModal
    :open="showDiscardChangesModal"
    :modal_config="props.modal_config"
    @close-modal="showDiscardChangesModal = false"
    @confirm="emit('close-modal')"
  />

  <Modal :open="props.open" :hideClose="true">
    <template #modal-header>
      <div v-if="props.modal_config.type !== 'owner_contact'">
        <div class="flex items-center">
          <Icon name="Pencil" class="w-4 h-4 mr-1 stroke-info" />
          <div class="text-sm font-inter font-semibold text-info">
            Editing
            {{ props.modal_config.type == "joint_contact" ? "Joint Contact" : "Entity Account" }}
          </div>
        </div>
        <div class="font-league tracking-[-.56px] font-semibold text-[1.75rem] text-strongest">
          {{ props.modal_config.record_name }}
        </div>
      </div>
      <div v-else class="">
        <div class="flex items-center">
          <Icon name="Pencil" class="w-4 h-4 mr-1 stroke-info" />
          <div class="text-sm font-inter font-semibold text-info">Editing</div>
        </div>
        <div class="font-league tracking-[-.56px] font-semibold text-[1.75rem] text-strongest">
          Editing {{ props.modal_config.subtab }}
        </div>
      </div>
    </template>

    <template #modal-body>
      <div class="p-2 lg:p-6">
        <div v-if="props.modal_config.type !== 'owner_contact'">
          <TabView
            :tabs="props.modal_config.tabs"
            :activeTab="activeProfileTab"
            @update:activeTab="(key) => (activeProfileTab = key)"
          >
            <template
              v-for="subtab in props.modal_config.tabs"
              :key="subtab.slot_name"
              #[subtab.slot_name]
            >
              <div v-if="props.modal_config.groups?.includes(subtab.label)">
                <Form
                  :schema_array="
                    props.modal_config.schema.filter(
                      (item) => item.record_detail_group === subtab.label
                    )
                  "
                  :state_object="getStateObject(subtab.label)"
                  :record_id="props.modal_config.record_id"
                  variant="secondary"
                />
              </div>
            </template>
          </TabView>
        </div>
        <div v-else>
          <div
            v-if="props.modal_config.subtab == 'Mailing Address'"
            class="bg-container-bg py-5 flex items-center px-3 mb-[0.875rem] border-2 rounded-xl"
            :class="
              mailing_same_as_residential ? 'border-radio-checkbox-outline' : 'border-container-bg'
            "
          >
            <div @click="handleMailingSameAsResidentialChange" class="mr-5">
              <Icon
                name="CircleCheck"
                class="w-6 h-6 fill-radio-checkbox-checked dark:fill-white stroke-container-bg"
                v-if="mailing_same_as_residential"
              />
              <Icon name="Circle" class="w-6 h-6 stroke-radio-checkbox-outline" v-else />
            </div>

            <div class="font-inter text-lg font-semibold text-strongest">
              Same as residential address
            </div>
          </div>

          <Form
            :schema_array="
              props.modal_config.schema.filter(
                (item) => item.record_detail_group === props.modal_config.subtab
              )
            "
            :state_object="getStateObject(props.modal_config.subtab)"
            variant="secondary"
            :record_id="props.modal_config.record_id"
            :is_disabled="
              mailing_same_as_residential && props.modal_config.subtab === 'Mailing Address'
            "
          />
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="flex justify-end gap-2">
        <Button @click="handleConfirmClose()" variant="paper-primary" size="sm" class="">
          <template #label>Cancel</template>
        </Button>
        <Button @click="handleConfirm()" variant="primary-grey" size="sm" class="">
          <template #label>Confirm</template>
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
// IMPORTS
import { ref, watch } from "vue";
import { Modal, TabView, Form, Icon, Button } from "@/components";
import { useCrudStore, useSchemaStore, useDataStore, useUIStore } from "@/stores";
import _ from "lodash";
import DiscardChangesModal from "./DiscardChangesModal.vue";

//STORES
const crudStore = useCrudStore();
const dataStore = useDataStore();
const schemaStore = useSchemaStore();
const UIStore = useUIStore();

//EMITS AND PROPS
const emit = defineEmits(["close-modal", "confirm"]);
const props = defineProps({
  open: { type: Boolean, required: true },
  modal_config: { type: Object, required: true },
});

//REFS
const mailing_same_as_residential = ref(false);
const activeProfileTab = ref("personal.personal_information");
const showDiscardChangesModal = ref(false);

//WATCHERS
watch(
  () => props.modal_config.open,
  (is_open) => {
    if (is_open) {
      let found_tab = props.modal_config.tabs.find(
        (tab) => tab.label === props.modal_config.subtab
      );

      if (found_tab) {
        activeProfileTab.value = found_tab.slot_name;
      } else {
        activeProfileTab.value = props.modal_config.tabs[0].slot_name;
      }

      let contact = dataStore.contact;

      if (
        contact.state == contact.ma_state &&
        contact.city == contact.ma_city &&
        contact.street1 == contact.ma_street1 &&
        contact.street2 == contact.ma_street2 &&
        contact.zip_code == contact.ma_zip_code
      ) {
        mailing_same_as_residential.value = true;
      } else {
        mailing_same_as_residential.value = false;
      }
    } else {
      schemaStore.show_input_errors = false;
    }
  },
  { immediate: true }
);

function handleConfirmClose() {
  let values_changed = checkForChanges();
  let id_info_changed = checkForChanges("id_info");
  if (!values_changed && !id_info_changed) {
    emit("close-modal");
    return;
  }

  // show discard changes modal
  showDiscardChangesModal.value = true;
}

// COMPONENT LOGIC AND FUNCTIONS
async function handleConfirm() {
  try {
    let values_changed = checkForChanges();
    let id_info_changed = checkForChanges("id_info");

    if (!values_changed && !id_info_changed) {
      emit("close-modal");
      return;
    }

    let object = props.modal_config.object_to_edit;
    object.id_info = props.modal_config.id_info;

    let _continue = schemaStore.validateFields(
      object,
      props.modal_config.schema,
      props.modal_config.record_id,
      true,
      props.modal_config.type,
      props.modal_config.original_object
    );

    if (_continue.proceed) {
      if (props.modal_config.type === "owner_contact") {
        await updateOwner();
      } else if (props.modal_config.type === "joint_contact") {
        await updateJoint();
      } else if (props.modal_config.type === "entity_account") {
        await updateEntity();
      }

      await handleActivityLog(true);

      emit("confirm", {
        type: props.modal_config.type, // "joint_contact" | "entity_account"
        record_id: props.modal_config.record_id,
      });

      emit("close-modal");
    } else {
      let invalid_tab = _continue.invalid_groups[0]
        ? props.modal_config.tabs.find((tab) => tab.label === _continue.invalid_groups[0])
        : props.modal_config.tabs[0];

      activeProfileTab.value = invalid_tab.slot_name;

      await handleActivityLog(false, _continue);

      return;
    }
  } catch (e) {
    let object_type =
      props.modal_config.type === "owner_contact"
        ? "owner details"
        : props.modal_config.type === "joint_contact"
          ? "joint details"
          : "entity details";
    var alert = {
      type: "error",
      message: `Failed to update ${object_type}.`,
    };
    UIStore.animateNotificationAlert(alert);
    emit("close-modal");
  }
}

async function updateOwner() {
  let query = [
    {
      field_name: "record_id",
      operator: "eq",
      value: props.modal_config.record_id,
    },
  ];
  let update = props.modal_config.object_to_edit;
  if (props.modal_config.subtab && props.modal_config.subtab.includes("ID Information")) {
    let updated_obj = props.modal_config.id_info;
    update = {
      id_info: {
        id_number: updated_obj.id_number,
        id_issued_by: updated_obj.id_issued_by,
        id_issued_date: new Date(updated_obj.id_issued_date),
        id_expiration_date: new Date(updated_obj.id_expiration_date),
      },
    };
  }

  update = schemaStore.addCreatedUpdatedFields(update, false, true);

  let response = await crudStore.update("contacts", query, update);

  if (response.status !== 200) {
    throw new Error("Failed to update owner contact");
  }

  let account_query = [
    {
      field_name: "user_id",
      operator: "eq",
      value: dataStore.contact.user_id,
    },
    {
      field_name: "type",
      operator: "eq",
      value: "Regular",
    },
  ];

  let account_update = {};

  if (props.modal_config.subtab === "Personal Information") {
    let name = `${props.modal_config.object_to_edit.first_name} ${props.modal_config.object_to_edit.last_name}`;
    let email = props.modal_config.object_to_edit.email;
    let phone = props.modal_config.object_to_edit.phone;

    account_update = {
      name,
      phone,
      email,
    };
  }
  if (props.modal_config.subtab === "Residential Address") {
    // set country, state, city, street1, street2, zip_code
    account_update.country = props.modal_config.object_to_edit.country;
    account_update.state = props.modal_config.object_to_edit.state;
    account_update.city = props.modal_config.object_to_edit.city;
    account_update.street1 = props.modal_config.object_to_edit.street1;
    account_update.street2 = props.modal_config.object_to_edit.street2;
    account_update.zip_code = props.modal_config.object_to_edit.zip_code;
  }

  if (Object.keys(account_update).length > 0) {
    account_update = schemaStore.addCreatedUpdatedFields(account_update, false, true);
    let account_response = await crudStore.update("accounts", account_query, account_update);

    if (account_response.status !== 200) {
      throw new Error("Failed to update owner account");
    }
  }
}

async function updateJoint() {
  let update = props.modal_config.object_to_edit;
  update = schemaStore.addCreatedUpdatedFields(update, false, true);

  let query = [
    {
      field_name: "record_id",
      operator: "eq",
      value: props.modal_config.record_id,
    },
  ];

  let response = await crudStore.update("contacts", query, update);

  if (response.status !== 200) {
    throw new Error("Failed to update joint contact");
  }
}

async function updateEntity() {
  let update = props.modal_config.object_to_edit;
  update = schemaStore.addCreatedUpdatedFields(update, false, true);

  let query = [
    {
      field_name: "record_id",
      operator: "eq",
      value: props.modal_config.record_id,
    },
  ];

  let response = await crudStore.update("accounts", query, update);

  if (response.status !== 200) {
    throw new Error("Failed to update entity account");
  }
}

function handleMailingSameAsResidentialChange() {
  mailing_same_as_residential.value = !mailing_same_as_residential.value;

  if (mailing_same_as_residential.value) {
    syncMailingToResidential();
  }
}

function syncMailingToResidential() {
  let contact = dataStore.contact;
  props.modal_config.object_to_edit.ma_state = contact.state;
  props.modal_config.object_to_edit.ma_city = contact.city;
  props.modal_config.object_to_edit.ma_street1 = contact.street1;
  props.modal_config.object_to_edit.ma_street2 = contact.street2;
  props.modal_config.object_to_edit.ma_zip_code = contact.zip_code;
  props.modal_config.object_to_edit.ma_country = contact.country;
}

watch(
  () => [
    dataStore.contact.street1,
    dataStore.contact.street2,
    dataStore.contact.city,
    dataStore.contact.state,
    dataStore.contact.zip_code,
    dataStore.contact.country,
  ],
  () => {
    if (
      mailing_same_as_residential.value &&
      props.modal_config.type === "owner_contact" &&
      props.modal_config.subtab === "Mailing Address"
    ) {
      syncMailingToResidential();
    }
  }
);

function getStateObject(group) {
  if (group == "ID Information") {
    return props.modal_config.id_info;
  }
  return props.modal_config.object_to_edit;
}

function checkForChanges(object) {
  if (object == "id_info") {
    let original = props.modal_config.original_id_info;
    let current = props.modal_config.id_info;
    let changed = false;

    for (let key in original) {
      if (original[key] !== current[key]) {
        changed = true;
        break;
      }
    }

    return changed;
  } else {
    let original = props.modal_config.original_object;
    let current = props.modal_config.object_to_edit;
    let changed = false;

    for (let key in original) {
      if (original[key] !== current[key]) {
        changed = true;
        break;
      }
    }

    return changed;
  }
}

async function handleActivityLog(valid, validation_details) {
  let status = valid ? "Completed" : "Failed Validation";
  let type =
    props.modal_config.type === "owner_contact"
      ? "Owner"
      : props.modal_config.type === "joint_contact"
        ? "Joint"
        : "Entity";

  //log active tab

  let substep =
    type == "Owner"
      ? `Edit ${activeProfileTab.value} for Owner`
      : `Edit ${type} Details, ${type} ID: ${props.modal_config.record_id}`;

  let log = {
    step: "Profile Page",
    substep: substep,
    status: status,
    message: "Editing " + type + " with ID: " + props.modal_config.record_id,
    url: window.location.href,
    validation_details: valid ? null : JSON.stringify(validation_details),
  };

  log = dataStore.setDefaultFields(log, "investoractivity");
  await crudStore.insert("investoractivity", [log]);
}
</script>
